import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { ClientError } from '../../api/apiError';
import {
  getRegions as restGetRegions,
  getStatus as restGetStatus,
} from '../../api/Rest/system';
import { getCaptcha as restGetCaptcha } from '../../api/Rest/users';
import { StorageValue } from '../../models/Store';
import {
  Regions,
  SystemStoreAction,
  SystemStoreState,
} from '../../models/SystemStore/SystemStore';
import { dateTimeReviver } from '../../utils/json';
import { UiLocale } from '../../models/Base';

type Store = SystemStoreState & SystemStoreAction;

const initializer: StateCreator<Store, [], []> = (set, get) => {
  const getStatus: SystemStoreAction['getStatus'] = () => {
    return get().status;
  };

  const fetchStatus: SystemStoreAction['fetchStatus'] = async (options?) => {
    const { ok, data, error } = await restGetStatus(options);

    if (ok) {
      set({ status: data });
    } else if (error instanceof ClientError) {
      if (error.errorCode === 440) {
        const errorInfo = error.getErrorInfo({
          message: 'please upgrade your app',
        });
        if (errorInfo && 'systemStatus' in errorInfo) {
          set({ status: errorInfo.systemStatus });
        }
      }
    }
  };

  const fetchRegions: SystemStoreAction['fetchRegions'] = async (
    params,
    options?,
  ) => {
    if (params?.locale !== get().regionLocale) {
      const { ok, data } = await restGetRegions(params, options);

      if (ok) {
        set({ regions: data.regions, regionLocale: params?.locale });
      }
    }
  };

  const fetchCaptcha: SystemStoreAction['fetchCaptcha'] = async (options?) => {
    const { ok, data } = await restGetCaptcha(options);
    if (ok) {
      return data;
    }
    return undefined;
  };

  return {
    getStatus,
    fetchStatus,
    fetchRegions,
    fetchCaptcha,
    regionLists: {},
  };
};

const persistOptions: PersistOptions<Store, Partial<SystemStoreState>> = {
  name: 'system-storage',
  // getStorage: () => AsyncStorage,
  deserialize: (str: string): StorageValue<Partial<SystemStoreState>> => {
    try {
      const res = JSON.parse(
        str,
        dateTimeReviver,
      ) as StorageValue<SystemStoreState>;
      return {
        ...res,
        state: {
          ...res.state,
        },
      };
    } catch (err) {
      console.error('Fail to deserialize system-storage', str);
      console.error(err);
      return { state: {} };
    }
  },
};

export const useSystemStore = create<Store>()(
  persist(initializer, persistOptions),
);

export const getRegionsList = async (locale: UiLocale): Promise<Regions> => {
  const { regionLists } = useSystemStore.getState();

  if (regionLists[locale]) {
    return regionLists[locale]!;
  }

  const { ok, data } = await restGetRegions({ locale });

  if (ok) {
    useSystemStore.setState((state) => ({
      regionLists: {
        ...state.regionLists,
        [locale]: data.regions,
      },
    }));
    return data.regions;
  }

  throw new Error('Failed to fetch regions');
};
